/**
 * Courses Management Component
 * Coláiste Lurgan Management System - Course CRUD operations
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { useToast } from '../../hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useUserPermissions } from '../../hooks/useAuth';
import {
  useCourses, useDeleteCourse, useArchiveCourse, useUnarchiveCourse, useDismissArchiveNudge,
} from '../../hooks/useCourses';
import CourseModal from './CourseModal';
import {
  BookOpen,
  Plus,
  Pencil,
  Trash2,
  Calendar,
  Users,
  Archive,
  ArchiveRestore,
  AlertTriangle,
  X,
} from 'lucide-react';

export function CoursesManagement() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { isAdmin } = useUserPermissions();
  const [showArchived, setShowArchived] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [courseToDelete, setCourseToDelete] = useState(null);
  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [courseToEdit, setCourseToEdit] = useState(null);

  // Archive/unarchive triple-lock dialog state
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiveMode, setArchiveMode] = useState('archive'); // 'archive' | 'unarchive'
  const [confirmationPhrase, setConfirmationPhrase] = useState('');
  const [archivePassword, setArchivePassword] = useState('');
  const [archiveErrorMessage, setArchiveErrorMessage] = useState('');
  const [dismissedNudges, setDismissedNudges] = useState(new Set());

  // Mutations
  const deleteCourseMutation = useDeleteCourse();
  const archiveCourseMutation = useArchiveCourse();
  const unarchiveCourseMutation = useUnarchiveCourse();
  const dismissArchiveNudgeMutation = useDismissArchiveNudge();

  // Get courses data with better error handling
  const {
    data: coursesResponse,
    isLoading,
    isError,
    error
  } = useCourses({}, {
    // Graceful error handling - don't treat empty DB as error
    retry: (failureCount, error) => {
      // Don't retry on 404 (no courses) or 500 from empty DB
      if (error?.response?.status === 404) return false;
      if (error?.response?.status === 500) return false;
      return failureCount < 3;
    },
    // Default to empty state if API fails
    onError: () => {}
  });

  // Handle both paginated and non-paginated response formats
  const courses = Array.isArray(coursesResponse)
    ? coursesResponse
    : (coursesResponse?.results || []);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '—';
    return new Intl.NumberFormat('en-IE', { 
      style: 'currency', 
      currency: 'EUR' 
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-IE');
  };

  // Course year, derived from start_date — Course has no stored `year` field
  const getCourseYear = (course) => {
    if (!course.start_date) return '—';
    return new Date(course.start_date).getFullYear();
  };

  // Check if course can be deleted (no students enrolled)
  const canDeleteCourse = (course) => {
    // Course can be deleted if no students are enrolled
    return (course.enrolled_count || 0) === 0;
  };

  // Get course status based on dates
  const getCourseStatus = (course) => {
    if (!course.start_date || !course.end_date) return 'unknown';

    const now = new Date();
    const startDate = new Date(course.start_date);
    const endDate = new Date(course.end_date);

    if (now < startDate) {
      return 'upcoming';
    } else if (now >= startDate && now <= endDate) {
      return 'active';
    } else {
      return 'completed';
    }
  };

  // Get status badge variant and text
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'upcoming':
        return { variant: 'secondary', text: t('courses.status.upcoming', 'Upcoming') };
      case 'active':
        return { variant: 'default', text: t('courses.status.active', 'Active') };
      case 'completed':
        return { variant: 'outline', text: t('courses.status.completed', 'Completed') };
      default:
        return { variant: 'secondary', text: t('courses.status.unknown', 'Unknown') };
    }
  };

  // Handle delete course
  const handleDeleteCourse = (course) => {
    setCourseToDelete(course);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteCourse = async () => {
    if (!courseToDelete) return;

    try {
      await deleteCourseMutation.mutateAsync(courseToDelete.uuid);
      toast({
        title: t('courses.deleted'),
        description: t('courses.deleteSuccess', { name: courseToDelete.name }),
      });
      setDeleteDialogOpen(false);
      setCourseToDelete(null);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('courses.deleteError'),
        variant: 'destructive',
      });
    }
  };

  // Handle archive/unarchive (triple-lock)
  const openArchiveDialog = (course, mode) => {
    setArchiveTarget(course);
    setArchiveMode(mode);
    setConfirmationPhrase('');
    setArchivePassword('');
    setArchiveErrorMessage('');
    setArchiveDialogOpen(true);
  };

  const closeArchiveDialog = () => {
    setArchiveDialogOpen(false);
    setArchiveTarget(null);
    setConfirmationPhrase('');
    setArchivePassword('');
    setArchiveErrorMessage('');
  };

  const confirmArchiveAction = async () => {
    if (!archiveTarget) return;
    setArchiveErrorMessage('');

    const mutation = archiveMode === 'archive' ? archiveCourseMutation : unarchiveCourseMutation;

    try {
      const result = await mutation.mutateAsync({
        id: archiveTarget.uuid,
        confirmation_phrase: confirmationPhrase,
        password: archivePassword,
      });
      toast({
        title: archiveMode === 'archive'
          ? t('courses.archive.success', 'Course archived')
          : t('courses.archive.unarchiveSuccess', 'Course unarchived'),
        description: archiveMode === 'archive'
          ? t('courses.archive.successMessage', '{{count}} student(s) archived.', { count: result.archived_count })
          : t('courses.archive.unarchiveSuccessMessage', '{{count}} student(s) restored.', { count: result.unarchived_count }),
        variant: 'success',
      });
      closeArchiveDialog();
    } catch (error) {
      const data = error?.response?.data;
      const message = data?.confirmation_phrase || data?.password || data?.detail
        || t('courses.archive.genericError', 'Something went wrong. Please try again.');
      setArchiveErrorMessage(message);
    }
  };

  const handleDismissNudge = (course) => {
    setDismissedNudges((prev) => new Set(prev).add(course.uuid));
    dismissArchiveNudgeMutation.mutate(course.uuid);
  };

  // Handle add course
  const handleAddCourse = () => {
    setCourseToEdit(null);
    setCourseModalOpen(true);
  };

  // Handle edit course
  const handleEditCourse = (course) => {
    setCourseToEdit(course);
    setCourseModalOpen(true);
  };

  // Handle course modal success
  const handleCourseModalSuccess = (courseData, isEditing) => {
    if (isEditing) {
      toast({
        title: t('courses.updated'),
        description: t('courses.updateSuccess', { name: courseData.name }),
        variant: 'success',
      });
    } else {
      toast({
        title: t('courses.created'),
        description: t('courses.createSuccess', { name: courseData.name }),
        variant: 'success',
      });
    }
  };

  // Only show error if it's a real error (not just empty database)
  if (isError && error?.response?.status !== 500) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-destructive">
            {t('error.loadingCourses', 'Error loading courses. Please try again.')}
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Error: {error?.response?.status} - {error?.message}
          </p>
        </CardContent>
      </Card>
    );
  }

  const visibleCourses = showArchived
    ? courses
    : courses.filter((course) => !course.has_archived_students);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              {t('courses.management.title', 'Course Management')}
            </CardTitle>
            <CardDescription>
              {t('courses.management.description', 'Manage course offerings, capacity, and pricing')}
            </CardDescription>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch
                id="show-archived-courses"
                checked={showArchived}
                onCheckedChange={setShowArchived}
              />
              <Label htmlFor="show-archived-courses" className="text-sm text-muted-foreground cursor-pointer">
                {t('courses.archive.showArchived', 'Show archived')}
              </Label>
            </div>
            <Button className="gap-2" onClick={handleAddCourse}>
              <Plus className="h-4 w-4" />
              {t('courses.actions.addCourse', 'Add Course')}
            </Button>
          </div>
        </div>
      </CardHeader>

      {/* "Ready to archive" nudges — one per eligible course, dismissible */}
      {isAdmin && courses
        .filter((course) => course.ready_to_archive && !dismissedNudges.has(course.uuid))
        .map((course) => (
          <div
            key={`nudge-${course.uuid}`}
            className="mx-6 mb-2 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-900 px-3 py-2 text-sm text-amber-800 dark:text-amber-300"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              <span>
                {t('courses.archive.nudge', '"{{courseName}}" finished over a week ago and is ready to archive.', { courseName: course.name })}
              </span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="h-7 gap-1"
                onClick={() => openArchiveDialog(course, 'archive')}
              >
                <Archive className="h-3 w-3" />
                {t('courses.archive.archiveNow', 'Archive Now')}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0"
                onClick={() => handleDismissNudge(course)}
                aria-label={t('common.dismiss', 'Dismiss')}
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}

      <CardContent className="p-0">
          {isLoading ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {t('loading.courses', 'Loading courses...')}
              </p>
            </div>
          ) : courses.length === 0 ? (
            <div className="text-center py-8">
              <BookOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {t('courses.empty', 'No courses configured yet. Add your first course to get started.')}
              </p>
            </div>
          ) : visibleCourses.length === 0 ? (
            <div className="text-center py-8">
              <Archive className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {t('courses.archive.allHidden', 'All courses have archived students. Toggle "Show archived" to see them.')}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('courses.fields.name', 'Course Name')}</TableHead>
                  <TableHead>{t('courses.fields.year', 'Year')}</TableHead>
                  <TableHead>{t('courses.fields.dates', 'Dates')}</TableHead>
                  <TableHead>{t('courses.fields.registered', 'Registered')}</TableHead>
                  <TableHead>{t('courses.fields.price', 'Price')}</TableHead>
                  <TableHead>{t('courses.fields.status', 'Status')}</TableHead>
                  <TableHead className="text-right">{t('common.actions', 'Actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleCourses.map((course) => (
                  <TableRow key={course.uuid}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{course.name}</div>
                        {course.description && (
                          <div className="text-sm text-muted-foreground line-clamp-1">
                            {course.description}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {getCourseYear(course)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(course.start_date)} - {formatDate(course.end_date)}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3 text-green-600" />
                          <span className="font-medium">{course.enrolled_count || 0}</span>
                        </div>
                        <span className="text-muted-foreground">/</span>
                        <span className="text-sm text-muted-foreground">{course.student_capacity}</span>
                        {course.student_capacity > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {Math.round(((course.enrolled_count || 0) / course.student_capacity) * 100)}%
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="font-medium">{formatCurrency(course.price)}</div>
                        <div className="text-xs text-muted-foreground">
                          {formatCurrency(course.cinnire_price)} {t('courses.fields.cinnireLabel', '(Cinnire)')}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        {(() => {
                          const status = getCourseStatus(course);
                          const statusDisplay = getStatusDisplay(status);
                          return (
                            <Badge variant={statusDisplay.variant}>
                              {statusDisplay.text}
                            </Badge>
                          );
                        })()}
                        {course.has_archived_students && (
                          <Badge variant="outline" className="gap-1 text-xs text-muted-foreground">
                            <Archive className="h-3 w-3" />
                            {t('courses.archive.archivedBadge', 'Archived')}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="gap-1"
                          onClick={() => handleEditCourse(course)}
                        >
                          <Pencil className="h-3 w-3" />
                          {t('common.edit', 'Edit')}
                        </Button>
                        
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1 text-red-600 hover:text-red-800"
                          disabled={!canDeleteCourse(course)}
                          onClick={() => handleDeleteCourse(course)}
                        >
                          <Trash2 className="h-3 w-3" />
                          {t('common.delete', 'Delete')}
                        </Button>

                        {isAdmin && (
                          course.has_archived_students ? (
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-1"
                              onClick={() => openArchiveDialog(course, 'unarchive')}
                            >
                              <ArchiveRestore className="h-3 w-3" />
                              {t('courses.archive.unarchive', 'Unarchive')}
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              className="gap-1"
                              onClick={() => openArchiveDialog(course, 'archive')}
                            >
                              <Archive className="h-3 w-3" />
                              {t('courses.archive.archive', 'Archive')}
                            </Button>
                          )
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('courses.delete.title', 'Delete Course')}
            </DialogTitle>
            <DialogDescription>
              {t('courses.delete.message', 'Are you sure you want to delete "{{courseName}}"? This action cannot be undone.', { courseName: courseToDelete?.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDeleteDialogOpen(false)}
            >
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteCourse}
              disabled={deleteCourseMutation.isPending}
            >
              {deleteCourseMutation.isPending ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive / Unarchive Confirmation Dialog (triple-lock) */}
      <Dialog open={archiveDialogOpen} onOpenChange={(open) => { if (!open) closeArchiveDialog(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {archiveMode === 'archive'
                ? t('courses.archive.dialogTitle', 'Archive Course Roster')
                : t('courses.archive.unarchiveDialogTitle', 'Unarchive Course Roster')}
            </DialogTitle>
            <DialogDescription>
              {archiveMode === 'archive'
                ? t(
                    'courses.archive.dialogMessage',
                    'This will archive every student in "{{courseName}}". Their records stay fully intact and can be restored at any time by an administrator.',
                    { courseName: archiveTarget?.name }
                  )
                : t(
                    'courses.archive.unarchiveDialogMessage',
                    'This will restore every archived student in "{{courseName}}" back to the active working view.',
                    { courseName: archiveTarget?.name }
                  )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="archive-confirmation-phrase">
                {t('courses.archive.typeToConfirm', 'Type the course name to confirm: {{courseName}}', { courseName: archiveTarget?.name })}
              </Label>
              <Input
                id="archive-confirmation-phrase"
                value={confirmationPhrase}
                onChange={(e) => setConfirmationPhrase(e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="archive-password">
                {t('courses.archive.passwordLabel', 'Re-enter your password')}
              </Label>
              <Input
                id="archive-password"
                type="password"
                value={archivePassword}
                onChange={(e) => setArchivePassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            {archiveErrorMessage && (
              <p className="text-sm text-destructive">{archiveErrorMessage}</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeArchiveDialog}>
              {t('common.cancel', 'Cancel')}
            </Button>
            <Button
              variant={archiveMode === 'archive' ? 'destructive' : 'default'}
              onClick={confirmArchiveAction}
              disabled={
                confirmationPhrase !== archiveTarget?.name
                || !archivePassword
                || archiveCourseMutation.isPending
                || unarchiveCourseMutation.isPending
              }
            >
              {archiveCourseMutation.isPending || unarchiveCourseMutation.isPending
                ? t('common.saving', 'Saving...')
                : archiveMode === 'archive'
                  ? t('courses.archive.confirmArchive', 'Archive')
                  : t('courses.archive.confirmUnarchive', 'Unarchive')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Course Creation/Edit Modal */}
      <CourseModal
        isOpen={courseModalOpen}
        onClose={() => setCourseModalOpen(false)}
        course={courseToEdit}
        onSuccess={handleCourseModalSuccess}
      />
    </Card>
  );
}

export default CoursesManagement;